import React, { useState, useEffect } from 'react';
import { OrderIdBadge } from './shared';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '@/config/api';
import { useAuth } from '@/context/AuthContext';
import { X, Edit2, Save, Phone, Mail, MapPin, AlertCircle } from 'lucide-react';

interface Customer {
  id: number;
  name: string;
  phone: string;
  email: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  last_order_date?: string;
  total_orders: number;
  total_spent: number;
  notes?: string;
  measurements?: {
    chest?: number;
    waist?: number;
    hips?: number;
    shoulder?: number;
    sleeve?: number;
    neck?: number;
    inseam?: number;
  };
  photos?: Array<{
    id: number;
    photo_type: string;
    photo_url: string;
    thumbnail_url?: string;
    scope?: string;
    created_at?: string;
  }>;
  order_history?: Array<{
    id: number;
    order_number: string;
    date: string;
    type: string;
    status: string;
    amount: number;
  }>;
  is_linked: boolean;
}

const CustomerDetails: React.FC = () => {
  const { id: customerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customerId || !token) {
        setLoading(false);
        setError('Missing customer ID or authentication token');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE}/api/tailors/customers/${customerId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          let errorMessage = 'Failed to load customer details';
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch {
            // If response is not JSON, use status text
            errorMessage = response.statusText || errorMessage;
          }
          
          if (response.status === 404) {
            setError('Customer not found');
          } else {
            setError(`${errorMessage} (Status: ${response.status})`);
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setCustomer(data);
        setEditForm({ email: data.email || '', phone: data.phone || '' });
        setLoading(false);
      } catch (_err) {
        console.error('Error fetching customer details:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load customer details';
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId, token]);

  const handleEditClick = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (customer) {
      setEditForm({ email: customer.email || '', phone: customer.phone || '' });
    }
    setSaveError(null);
  };

  const handleSaveEdit = async () => {
    if (!customer || !token) return;
    
    setSaving(true);
    setSaveError(null);

    try {
      // Update customer email/phone via the update endpoint
      const response = await fetch(`${API_BASE}/api/tailors/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: editForm.email,
          phone: editForm.phone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update customer');
      }

      // Refresh customer data
      const updatedResponse = await fetch(`${API_BASE}/api/tailors/customers/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setCustomer(updatedData);
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error('Error updating customer:', err);
      setSaveError(err.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const handleNewOrder = () => {
    // Navigate to new order form with customer pre-selected
    navigate(`/business/orders/new?customerId=${customerId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-semibold">{error || 'Customer not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const canEditContact = !customer.is_linked; // Only allow editing if customer hasn't linked

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600 mr-4"
              >
                <span className="text-2xl">←</span>
              </button>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">MJ</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Customer Details</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={canEditContact ? handleEditClick : () => alert('Cannot edit: Customer has already linked their account. Contact information is managed by the customer.')}
                className={`${canEditContact ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2`}
                disabled={!canEditContact}
              >
                <Edit2 className="w-4 h-4" />
                Edit Customer
              </button>
              <button
                onClick={handleNewOrder}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                New Order
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Edit Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Customer</h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  {saveError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">
                  {customer.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>
                <p className="text-gray-600">{customer.phone}</p>
                {customer.email && (
                  <p className="text-gray-600">{customer.email}</p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {customer.phone}
                    </p>
                    {customer.email && (
                      <p className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {customer.email}
                      </p>
                    )}
                    {customer.address && (
                      <p className="flex items-start">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                        <span>
                          {customer.address}
                          {customer.city && `, ${customer.city}`}
                          {customer.state && `, ${customer.state}`}
                          {customer.postal_code && ` ${customer.postal_code}`}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Order Summary</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span>Total Orders:</span>
                      <span className="font-semibold">{customer.total_orders}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Total Spent:</span>
                      <span className="font-semibold">{formatCurrency(customer.total_spent)}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Last Order:</span>
                      <span className="font-semibold">{formatDate(customer.last_order_date)}</span>
                    </p>
                  </div>
                </div>

                {customer.notes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {customer.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Measurements and Order History */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photos */}
            {customer.photos && customer.photos.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {customer.photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition-colors">
                        <img
                          src={photo.thumbnail_url || photo.photo_url}
                          alt={`${photo.photo_type || 'photo'} view`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/200?text=Photo+Error';
                          }}
                        />
                      </div>
                      {photo.photo_type && (
                        <div className="mt-2 text-center">
                          <span className="text-xs font-medium text-gray-600 capitalize">
                            {photo.photo_type.replace('_', ' ')}
                          </span>
                          {photo.created_at && (
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(photo.created_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Measurements */}
            {customer.measurements && Object.values(customer.measurements).some(v => v !== null && v !== undefined) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Measurements</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {customer.measurements.chest && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Chest</div>
                      <div className="font-semibold">{customer.measurements.chest}"</div>
                    </div>
                  )}
                  {customer.measurements.waist && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Waist</div>
                      <div className="font-semibold">{customer.measurements.waist}"</div>
                    </div>
                  )}
                  {customer.measurements.hips && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Hips</div>
                      <div className="font-semibold">{customer.measurements.hips}"</div>
                    </div>
                  )}
                  {customer.measurements.shoulder && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Shoulder</div>
                      <div className="font-semibold">{customer.measurements.shoulder}"</div>
                    </div>
                  )}
                  {customer.measurements.sleeve && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Sleeve</div>
                      <div className="font-semibold">{customer.measurements.sleeve}"</div>
                    </div>
                  )}
                  {customer.measurements.neck && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Neck</div>
                      <div className="font-semibold">{customer.measurements.neck}"</div>
                    </div>
                  )}
                  {customer.measurements.inseam && (
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Inseam</div>
                      <div className="font-semibold">{customer.measurements.inseam}"</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order History */}
            {customer.order_history && customer.order_history.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order History</h3>
                <div className="space-y-3">
                  {customer.order_history.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            <OrderIdBadge id={order.order_number || String(order.id)} compact />
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                            order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <div>👕 {order.type}</div>
                          <div>📅 {formatDate(order.date)}</div>
                          <div>💰 {formatCurrency(order.amount)}</div>
                        </div>
                      </div>
                      <Link
                        to={`/order/${order.order_number || order.id}/details`}
                        className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm ml-4"
                      >
                        View Order
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;